import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const productInfoLabels = {
  productId: 'Product ID',
  productCode: 'Product Code',
  productName: 'Product Name',
  description: 'Description',
  productOwner: 'Product Owner',
  parentDomainId: 'Parent Domain ID',
  parentDomainName: 'Parent Domain',
  domainId: 'Domain ID',
  domainName: 'Domain',
  subDomainId: 'Sub Domain ID',
  subDomainName: 'Sub Domain',
  dataLayer: 'Data Layer',
  marketRegion: 'Market Region',
  marketCountry: 'Market Country',
  lineOfBusiness: 'Line of Business',
  costThreshold: 'Cost Threshold',
  sizeThreshold: 'Size Threshold',
  valueMapping: 'Value Mapping',
  azureServicesCost: 'Azure Services Cost',
  slaResponseTime: 'SLA Response Time',
  slaUptime: 'SLA Uptime',
  productTag: 'Product Tag',
  productDocumentationUrl: 'Product Documentation URL',
  minExpectedUsers: 'Min Expected Users',
  maxExpectedUsers: 'Max Expected Users',
  assetSource: 'Asset Source',
  status: 'Status',
};

const Publish = ({onPrevious, nextStep, id}) => {
  const [expandedSections, setExpandedSections] = useState({
    onboardingProduct: false,
    onboardingAsset: false,
    profiling: false,
    dataquality: false
  });
  const [loading, setLoading] = useState(true);
  const [productInfo, setProductInfo] = useState(null);
  const [assetInfo, setAssetInfo] = useState(null);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [expandedColumns, setExpandedColumns] = useState({});
  const [columnInfo, setColumnInfo] = useState([]);
  const [profilingResult, setProfilingResult] = useState([]);
  const [expandedProfiling, setExpandedProfiling] = useState({});
  const [expandedProfilingAsset, setExpandedProfilingAsset] = useState({});
  const [expandedProfilingColumn, setExpandedProfilingColumn] = useState({});
  const [dataQuality, setDataQuality] = useState([]);
  const [expandedDQAsset, setExpandedDQAsset] = useState({});
  const [expandedDQRule, setExpandedDQRule] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/publish-overview/${id}`)
      .then(res => {
        setProductInfo(res.data.productInfo);
        setAssetInfo(res.data.assetInfo);
        setColumnInfo(res.data.columnInfo || []);
        setProfilingResult(res.data.profilingResult || []);
        setDataQuality(res.data.dataQuality || []);
      })
      .catch(() => {
        setProductInfo(null);
        setAssetInfo(null);
        setColumnInfo([]);
        setProfilingResult([]);
        setDataQuality([]);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const toggleAsset = (idx) => {
    setExpandedAssets(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  const toggleColumn = (idx) => {
    setExpandedColumns(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  const toggleProfiling = (idx) => {
    setExpandedProfiling(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleProfilingAsset = (assetName) => {
    setExpandedProfilingAsset(prev => ({ ...prev, [assetName]: !prev[assetName] }));
  };
  const toggleProfilingColumn = (assetName, columnName) => {
    setExpandedProfilingColumn(prev => ({
      ...prev,
      [assetName]: {
        ...(prev[assetName] || {}),
        [columnName]: !(prev[assetName] && prev[assetName][columnName])
      }
    }));
  };

  const toggleDQAsset = (assetName) => {
    setExpandedDQAsset(prev => ({ ...prev, [assetName]: !prev[assetName] }));
  };
  const toggleDQRule = (assetName, ruleIdx) => {
    setExpandedDQRule(prev => ({
      ...prev,
      [assetName]: {
        ...(prev[assetName] || {}),
        [ruleIdx]: !(prev[assetName] && prev[assetName][ruleIdx])
      }
    }));
  };

  const handlePublish = async () => {
    if (!id) return;
    setPublishing(true);
    setPublishMessage("");
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/publish/${id}`);
      toast.success("Publish successful!");
      setTimeout(() => {
        navigate('/product-onboarding');
        window.location.reload();
      }, 1200);
    } catch (err) {
      toast.error("Publish failed. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  const AccordionSection = ({ title, sectionKey, children }) => (
    <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full px-6 py-4 bg-gradient-to-r from-teal-500 to-teal-500 text-white font-semibold text-left flex items-center justify-between hover:from-teal-700 hover:to-teal-800 transition-all duration-200"
      >
        <span>{title}</span>
        {expandedSections[sectionKey] ? (
          <ChevronDown size={20} className="transform transition-transform duration-200" />
        ) : (
          <ChevronRight size={20} className="transform transition-transform duration-200" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="p-6 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );

  // Helper to format label names
  const formatLabel = (label) => {
    return label
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderProductInfoTable = (info) => {
    if (!info) return null;
    // Simple fields (not arrays/objects)
    const simpleFields = Object.keys(info).filter(
      key => !['developmentCosts', 'supportCosts', 'operationCosts', 'sizeThreshold', 'slaResponseTime', 'status', 'productId', 'subDomainId', 'domainId', 'parentDomainId'].includes(key)
        && !Array.isArray(info[key]) && typeof info[key] !== 'object'
    );
    // Special object fields
    const sizeThreshold = info.sizeThreshold;
    const slaResponseTime = info.slaResponseTime;
    // Array fields
    const costFields = [
      { key: 'developmentCosts', label: 'Development Costs' },
      { key: 'supportCosts', label: 'Support Costs' },
      { key: 'operationCosts', label: 'Operation Costs' },
    ];
    return (
      <>
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {simpleFields.map((key) => (
                <tr key={key} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(key)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{Array.isArray(info[key]) ? info[key].join(', ') : String(info[key])}</td>
                </tr>
              ))}
              {sizeThreshold && (
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Size Threshold</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{sizeThreshold.value} {sizeThreshold.unit}</td>
                </tr>
              )}
              {slaResponseTime && (
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">SLA Response Time</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{slaResponseTime.value} {slaResponseTime.unit}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Cost Arrays */}
        {costFields.map(({ key, label }) => (
          Array.isArray(info[key]) && info[key].length > 0 && (
            <div key={key} className="mb-6">
              <div className="font-semibold mb-2">{label}</div>
              <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {info[key].map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-700">{row.startDate}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.endDate}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ))}
      </>
    );
  };

  const assetConfigFields = [
    'assetName',
    'assetDescription',
    'dataAssetOwner',
    'region',
    'market',
    'technicalDataSteward',
    'refreshFrequency',
    'completeness',
    'accuracy',
    'consistency',
    'sampleSize',
  ];

  const renderAssetInfoTable = (assetInfo) => {
    if (!assetInfo || (!assetInfo.selectedFolders && !assetInfo.uploadedFiles)) {
      return <div>No asset info available.</div>;
    }
    // Combine selectedFolders and uploadedFiles
    const allAssets = [
      ...(assetInfo.selectedFolders || []),
      ...(assetInfo.uploadedFiles || [])
    ];
    if (allAssets.length === 0) {
      return <div>No asset info available.</div>;
    }
    return (
      <div className="space-y-6">
        <div className="font-semibold text-lg mb-4">Asset Details</div>
        {allAssets.map((folder, idx) => (
          <div key={idx} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              onClick={() => toggleAsset(idx)}
              className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
            >
              <span>{folder.configuration?.assetName || folder.name || `Asset ${idx + 1}`}</span>
              {expandedAssets[idx] ? (
                <ChevronDown size={20} className="transform transition-transform duration-200" />
              ) : (
                <ChevronRight size={20} className="transform transition-transform duration-200" />
              )}
            </button>
            {expandedAssets[idx] && folder.configuration && (
              <div className="p-4 bg-gray-50">
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {assetConfigFields.map((key) => (
                        <tr key={key} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(key)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{folder.configuration[key] ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
        {/* Column Info Section */}
        {columnInfo && columnInfo.length > 0 && (
          <div className="mt-8">
            <div className="font-semibold text-lg mb-4">Column Selection Details</div>
            <div className="space-y-4">
              {columnInfo.map((col, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <button
                    onClick={() => toggleColumn(idx)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
                  >
                    <span>{col.name} ({col.path})</span>
                    {expandedColumns[idx] ? (
                      <ChevronDown size={20} className="transform transition-transform duration-200" />
                    ) : (
                      <ChevronRight size={20} className="transform transition-transform duration-200" />
                    )}
                  </button>
                  {expandedColumns[idx] && Array.isArray(col.columns) && col.columns.length > 0 && (
                    <div className="p-4 bg-gray-50">
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CDE</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '45%'}}>Description</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {col.columns.map((column, cidx) => (
                              <tr key={cidx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{column.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{column.type}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{String(column.cde)}</td>
                                <td className="px-6 py-4 text-sm text-gray-700" style={{width: '45%', whiteSpace: 'normal', wordBreak: 'break-word'}}>{column.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProfilingTable = (profilingResult) => {
    if (!profilingResult || profilingResult.length === 0) {
      return <div>No profiling results available.</div>;
    }
    // Group by asset name
    const grouped = {};
    for (const item of profilingResult) {
      if (!grouped[item.data_asset_name]) grouped[item.data_asset_name] = [];
      grouped[item.data_asset_name].push(item);
    }
    return (
      <div className="space-y-6">
        {Object.entries(grouped).map(([assetName, items], assetIdx) => {
          // Unique columns for this asset
          const uniqueColumns = [];
          const seenColumns = new Set();
          for (const item of items) {
            if (!seenColumns.has(item.column_name)) {
              seenColumns.add(item.column_name);
              uniqueColumns.push(item);
            }
          }
          return (
            <div key={assetName} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => toggleProfilingAsset(assetName)}
                className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
              >
                <span>{assetName}</span>
                {expandedProfilingAsset[assetName] ? (
                  <ChevronDown size={20} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={20} className="transform transition-transform duration-200" />
                )}
              </button>
              {expandedProfilingAsset[assetName] && (
                <div className="p-4 bg-gray-50 space-y-4">
                  {uniqueColumns.map((item, colIdx) => (
                    <div key={item.column_name} className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <button
                        onClick={() => toggleProfilingColumn(assetName, item.column_name)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
                      >
                        <span>{item.column_name}</span>
                        {expandedProfilingColumn[assetName] && expandedProfilingColumn[assetName][item.column_name] ? (
                          <ChevronDown size={20} className="transform transition-transform duration-200" />
                        ) : (
                          <ChevronRight size={20} className="transform transition-transform duration-200" />
                        )}
                      </button>
                      {expandedProfilingColumn[assetName] && expandedProfilingColumn[assetName][item.column_name] && (
                        <div className="p-4 bg-gray-50">
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {(() => {
                                  let parsed;
                                  try {
                                    parsed = item.result ? JSON.parse(item.result) : {};
                                  } catch {
                                    return <tr><td colSpan={2} className="px-6 py-4 text-sm text-red-500">Invalid result format</td></tr>;
                                  }
                                  const seenResult = new Set();
                                  return Object.entries(parsed).filter(([k]) => {
                                    if (seenResult.has(k)) return false;
                                    seenResult.add(k);
                                    return true;
                                  }).map(([k, v]) => (
                                    <tr key={k} className="hover:bg-gray-50 transition-colors">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(k)}</td>
                                      <td className="px-6 py-4 text-sm text-gray-700">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td>
                                    </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDataQualitySection = (dataQuality) => {
    if (!dataQuality || Object.keys(dataQuality).length === 0) {
      return <div>No data quality rules available.</div>;
    }
    return (
      <div className="space-y-8">
        {Object.entries(dataQuality).map(([assetName, dqObj]) => {
          const rules = dqObj.rules || [];
          const weightage = dqObj.weightage || {};
          // Group rules by check_type
          const assetLevelRules = rules.filter(r => r.check_type === 'Asset Level Check');
          const columnLevelRules = rules.filter(r => r.check_type === 'Column Level Check');
          return (
            <div key={assetName} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => toggleDQAsset(assetName)}
                className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
              >
                <span>{assetName}</span>
                {expandedDQAsset[assetName] ? (
                  <ChevronDown size={20} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={20} className="transform transition-transform duration-200" />
                )}
              </button>
              {expandedDQAsset[assetName] && (
                <div className="p-4 bg-gray-50 space-y-8">
                  {/* Asset Level Rules */}
                  {assetLevelRules.length > 0 && (
                    <div>
                      <div className="font-semibold text-lg mb-4">Asset Level Rules</div>
                      <div className="space-y-4">
                        {assetLevelRules.map((rule, idx) => (
                          <div key={rule.rules_id || idx} className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <button
                              onClick={() => toggleDQRule(assetName, `asset-${idx}`)}
                              className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
                            >
                              <span>{rule.rule_name}</span>
                              {expandedDQRule[assetName] && expandedDQRule[assetName][`asset-${idx}`] ? (
                                <ChevronDown size={20} className="transform transition-transform duration-200" />
                              ) : (
                                <ChevronRight size={20} className="transform transition-transform duration-200" />
                              )}
                            </button>
                            {expandedDQRule[assetName] && expandedDQRule[assetName][`asset-${idx}`] && (
                              <div className="p-4 bg-gray-50">
                                <div className="overflow-x-auto mb-4">
                                  <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {Object.entries(rule).filter(([key]) => key !== 'data_asset_id' && key !== 'rules_id' && key !== 'weightage').map(([key, value]) => (
                                        <tr key={key} className="hover:bg-gray-50 transition-colors">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(key)}</td>
                                          <td className="px-6 py-4 text-sm text-gray-700">{String(value)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {rule.weightage && Object.keys(rule.weightage).length > 0 && (
                              <div className="mt-4">
                                <div className="font-semibold mb-2">Weightage</div>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dimension</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {Object.entries(rule.weightage).map(([key, value]) => (
                                        <tr key={key} className="hover:bg-gray-50 transition-colors">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(key)}</td>
                                          <td className="px-6 py-4 text-sm text-gray-700">{String(value)}%</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Column Level Rules */}
                  {columnLevelRules.length > 0 && (
                    <div>
                      <div className="font-semibold text-lg mb-4">Column Level Rules</div>
                      <div className="space-y-4">
                        {columnLevelRules.map((rule, idx) => (
                          <div key={rule.rules_id || idx} className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <button
                              onClick={() => toggleDQRule(assetName, `column-${idx}`)}
                              className="w-full px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 font-semibold text-left flex items-center justify-between hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
                            >
                              <span>{rule.rule_name}</span>
                              {expandedDQRule[assetName] && expandedDQRule[assetName][`column-${idx}`] ? (
                                <ChevronDown size={20} className="transform transition-transform duration-200" />
                              ) : (
                                <ChevronRight size={20} className="transform transition-transform duration-200" />
                              )}
                            </button>
                            {expandedDQRule[assetName] && expandedDQRule[assetName][`column-${idx}`] && (
                              <div className="p-4 bg-gray-50">
                                <div className="overflow-x-auto mb-4">
                                  <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {Object.entries(rule).map(([key, value]) => (
                                        <tr key={key} className="hover:bg-gray-50 transition-colors">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(key)}</td>
                                          <td className="px-6 py-4 text-sm text-gray-700">{String(value)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Weightage Section */}
                  {Object.keys(weightage).length > 0 && (
                    <div>
                      <div className="font-semibold mb-2">Weightage</div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dimension</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(weightage).map(([key, value]) => (
                              <tr key={key} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatLabel(key)}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{String(value)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-[90%] mx-auto p-6 min-h-screen relative">
      <Toaster position="top-right" />
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <span className="text-lg font-semibold text-gray-600">Loading...</span>
        </div>
      ) : (
        <div className="space-y-6">
          <AccordionSection title="Onboarding Product" sectionKey="onboardingProduct">
            {renderProductInfoTable(productInfo)}
          </AccordionSection>
          <AccordionSection title="Onboarding Asset" sectionKey="onboardingAsset">
            {renderAssetInfoTable(assetInfo)}
          </AccordionSection>
          <AccordionSection title="Profiling" sectionKey="profiling">
            {renderProfilingTable(profilingResult)}
          </AccordionSection>
          <AccordionSection title="Data Quality" sectionKey="dataquality">
            {renderDataQualitySection(dataQuality)}
          </AccordionSection>
          <div className="flex justify-end pt-8 pb-4">
            <button
              className="px-8 py-3 bg-[#0000a0] hover:bg-[#0000a0] text-white font-bold rounded-lg shadow-lg transition-all duration-200 disabled:opacity-60"
              onClick={handlePublish}
              disabled={publishing || !id}
            >
              {publishing ? "Publishing..." : "Publish"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Publish;